import { useState } from 'react'
import type { InputHTMLAttributes, JSX, KeyboardEvent, ReactNode } from 'react'

/**
 * ══ PRIMITIVE Ô NHẬP — chép từ `proship-trans/src/components/ui.tsx` ══════════════════════════
 * Class Tailwind chép NGUYÊN VĂN (C12). Đặt ở `@rs/ui` vì cả tab Khách hàng lẫn tab Nhà cung cấp
 * đều dùng — để trong `routes/catalog/` thì tab NCC phải import chéo thư mục route.
 *
 * ⚠ `hotkeySave` TỪNG BỊ BỎ, NAY ĐÃ CÓ LẠI (G2). Task 19 bỏ nó với lý do đúng lúc đó — *"chỉ có
 *   nghĩa khi `Modal` bắt Ctrl/Cmd+S, mà `@rs/ui` chưa có `Modal` biểu mẫu"* — và hẹn dựng cùng
 *   lượt với `Modal`. G2 dựng `Modal` (xem `Modal.tsx`) nên lời hẹn đó tới hạn.
 *
 * ⚠ G2 cũng sửa CLASS của `Button` cho khớp bản cũ từng ký tự (`ui.tsx:69`). Bản Task 19 viết
 *   `disabled:opacity-40 disabled:cursor-not-allowed`; bản cũ là `disabled:opacity-50`, không có
 *   `cursor-not-allowed`. Lệch nhỏ nhưng có ở MỌI nút bị khoá của hệ thống, và §7 áp cho đúng thứ
 *   nhìn thấy được. Thứ tự class cũng đảo về đúng bản cũ để hai bên so diff được bằng mắt.
 */
type Variant = 'primary' | 'sky' | 'success' | 'ghost' | 'danger' | 'softSky' | 'softViolet'

export const Button = ({
  children, onClick, variant = 'primary', type = 'button', disabled, size = 'md',
  hotkeySave = false, title,
}: {
  children: ReactNode
  onClick?: () => void
  variant?: Variant
  type?: 'button' | 'submit'
  disabled?: boolean
  size?: 'md' | 'sm'
  /** Đánh dấu đây là nút LƯU CHÍNH của phiếu — Ctrl/Cmd+S trong Modal sẽ bấm hộ nút này (user chốt
   *  20/08). Chỉ đặt true cho ĐÚNG 1 nút mỗi modal, thường là nút lưu/xác nhận cuối cùng. */
  hotkeySave?: boolean
  /** Tooltip khi rê chuột — thường dùng để giải thích LÝ DO nút bị khóa (disabled). */
  title?: string
}): JSX.Element => {
  const styles: Record<Variant, string> = {
    primary: 'bg-gradient-to-r from-brand-500 to-brand-600 text-white hover:from-brand-600 hover:to-brand-700',
    sky: 'bg-sky-500 text-white hover:bg-sky-600',
    success: 'bg-emerald-500 text-white hover:bg-emerald-600',
    ghost: 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50',
    danger: 'bg-white border border-rose-300 text-rose-600 hover:bg-rose-50',
    softSky: 'bg-sky-50 border border-sky-200 text-sky-700 hover:bg-sky-100',
    softViolet: 'bg-violet-50 border border-violet-200 text-violet-700 hover:bg-violet-100',
  }
  const sizes: Record<'md' | 'sm', string> = {
    md: 'px-3.5 py-2 rounded-lg text-sm',
    sm: 'px-2 py-1 rounded-md text-[11px]',
  }
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-hotkey-save={hotkeySave ? true : undefined}
      className={`font-semibold transition disabled:opacity-50 ${sizes[size]} ${styles[variant]}`}
    >
      {children}
    </button>
  )
}

export const Field = ({ label, children }: { label: ReactNode; children: ReactNode }): JSX.Element => (
  <div>
    <label className="block text-xs font-semibold text-slate-500 mb-1">{label}</label>
    {children}
  </div>
)

/** Class ô nhập dùng chung — xuất ra để `Select.tsx` dùng CHUNG MỘT chuỗi với `Input`.
 *  Chép lại ở tệp khác là mời hai ô nhập cạnh nhau lệch nhau một pixel.
 *  ⚠ PHẢI KHỚP `@utility input` trong `apps/web/src/index.css` — bản cũ ghi rõ ràng buộc này
 *  (`index.css:24`), và ngày nó lệch là ngày các ô trong cùng một hàng cao thấp khác nhau. */
export const fieldCls =
  'w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100 bg-white'

export const Input = (
  { className = '', ...props }: InputHTMLAttributes<HTMLInputElement>,
): JSX.Element => <input {...props} className={`${fieldCls} ${className}`} />
/**
 * ══ Ô NHẬP SỐ — chép `ui.tsx:263-360` ═══════════════════════════════════════════════════════════
 *
 * ⚠ G2 SỬA BA LỆCH HÀNH VI so với bản Task 19 (kế hoạch §5.2). Cả ba đều là *"cách hoạt động"*
 * nên §7 áp, và cả ba đều nhìn thấy được ở mọi ô tiền của hệ thống:
 *
 *  ① **Dấu nghìn hiện NGAY LÚC GÕ.** Bản Task 19 làm `setBuf(e.target.value)` — giữ chuỗi THÔ, chỉ
 *     format khi rời ô ⇒ gõ 7 số ra `1000000`, bản cũ ra `1.000.000`. Bản cũ format ngay trong
 *     `onChange`: `setBuf(Number(digits).toLocaleString('vi-VN'))`.
 *  ② **`decimal` · `allowNegative`.** Không có hai prop này thì KHÔNG nhập được định mức lẻ
 *     (0.35 L/km) và KHÔNG nhập được số âm (phân bổ giảm trừ −30.000) — hai nghiệp vụ có thật.
 *  ③ **Ô bị KHOÁ phải trông như bị khoá** (`opacity-50 cursor-not-allowed bg-slate-100`). Thiếu nó
 *     thì ô tiền không sửa được trông y hệt ô sửa được.
 *
 * ⚠ `buf` giữ đúng chuỗi vừa gõ. Không có nó thì `display` tính lại từ `value` đã format sau MỖI ký
 *   tự, React set lại `.value` bằng JS và trình duyệt đẩy caret về cuối ô — gõ giữa một số dài là
 *   con trỏ "nhảy" (user báo 27/08 ở bản cũ: "nhập 1 phát là nhảy").
 */
export const NumberInput = ({
  value, onChange, className = '', placeholder, ariaLabel,
  decimal = false, allowNegative = false, disabled = false, onBlur, onKeyDown,
}: {
  value: number | string
  onChange: (n: number) => void
  className?: string
  placeholder?: string
  /** Nhãn trợ năng cho ô KHÔNG có `<label>` đi kèm — ô sửa tại chỗ trong bảng là ca điển hình. */
  ariaLabel?: string
  /** true = cho phép nhập số thập phân (VD định mức L/km 0.35) — mặc định false = số nguyên có dấu phân cách nghìn */
  decimal?: boolean
  /** true = cho nhập số ÂM (VD phân bổ giảm trừ −30.000) — mặc định false = chỉ ≥ 0 */
  allowNegative?: boolean
  disabled?: boolean
  /** rời ô — dùng cho ô sửa TẠI CHỖ trong bảng (lưu khi blur) */
  onBlur?: () => void
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void
}): JSX.Element => {
  const [buf, setBuf] = useState<string | null>(null)
  const fmtInt = (v: number | string): string =>
    v === '' || v === null || v === undefined ? '' : (Number(v) || 0).toLocaleString('vi-VN')
  const display = decimal
    ? buf ?? (value === '' || value === null || value === undefined ? '' : String(value))
    : buf ?? fmtInt(value)
  return (
    <input
      type="text"
      inputMode={decimal ? 'decimal' : 'numeric'}
      value={display}
      placeholder={placeholder}
      aria-label={ariaLabel}
      disabled={disabled}
      onChange={(e) => {
        if (decimal) {
          let cleaned = e.target.value.replace(/[^\d.]/g, '')
          const firstDot = cleaned.indexOf('.')
          if (firstDot !== -1) cleaned = cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '')
          setBuf(cleaned)
          onChange(cleaned === '' || cleaned === '.' ? 0 : Number(cleaned))
        } else if (allowNegative) {
          const raw = e.target.value.replace(/[^\d-]/g, '')
          const neg = raw.trim().startsWith('-')
          const digits = raw.replace(/-/g, '')
          setBuf((neg ? '-' : '') + (digits !== '' ? Number(digits).toLocaleString('vi-VN') : ''))
          onChange(digits === '' ? 0 : (neg ? -Number(digits) : Number(digits)))
        } else {
          const digits = e.target.value.replace(/\D/g, '')
          setBuf(digits !== '' ? Number(digits).toLocaleString('vi-VN') : '')
          onChange(digits === '' ? 0 : Number(digits))
        }
      }}
      onBlur={() => { setBuf(null); onBlur?.() }}
      onKeyDown={onKeyDown}
      className={`${fieldCls} ${className} ${disabled ? 'opacity-50 cursor-not-allowed bg-slate-100' : ''}`}
    />
  )
}
